import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Loader } from "lucide-react";
import { localizedUrl } from "../../../lib/fetchLocalized";
import FundraisingCard from "./FundraisingCard";
import { missionSiteFallback } from "../../../data/missionContent";

const DEFAULT_CAMPAIGN_IMAGE = "/assets/agm/polyclinic.jpg";
const donateApiUrl = import.meta.env.VITE_APP_DONATE_API_URL?.replace(/\/$/, "");

const fallbackCampaigns = missionSiteFallback.programPillars.slice(0, 5).map((program) => ({
  id: program.id,
  title: program.title,
  backgroundImage: program.image,
  achieved: 0,
  goal: 0,
  donors: 0,
  description: program.description,
  additionalText: program.eyebrow,
  bulletPoints: program.metrics,
  isActive: true,
}));

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function toStringArray(value) {
  if (Array.isArray(value)) {
    return value
      .map(String)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function getCampaignsFromPayload(payload) {
  const data = payload?.data ?? payload?.campaigns ?? payload?.donations ?? payload;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
}

function normalizeCampaign(campaign, index) {
  if (!campaign || typeof campaign !== "object") return null;

  const title = String(campaign.title || "").trim();
  if (!title) return null;

  const id = String(campaign._id || campaign.id || `campaign-${index}`).trim();

  return {
    id,
    title,
    backgroundImage: campaign.backgroundImage || DEFAULT_CAMPAIGN_IMAGE,
    achieved: toNumber(campaign.achieved),
    goal: toNumber(campaign.goal),
    donors: toNumber(campaign.donors),
    description: String(campaign.description || "").trim(),
    additionalText: String(campaign.additionalText || "").trim(),
    bulletPoints: toStringArray(campaign.bulletPoints),
    isActive: campaign.isActive !== false,
    isDeleted: campaign.isDeleted === true,
  };
}

const FundraisingSection = () => {
  const { t } = useTranslation();
  const [fundraisingData, setFundraisingData] = useState(fallbackCampaigns);
  const [isLoading, setIsLoading] = useState(Boolean(donateApiUrl));
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");

  useEffect(() => {
    let cancelled = false;

    async function fetchFundraisingData() {
      if (!donateApiUrl) {
        setFundraisingData(fallbackCampaigns);
        setStatusMessage(t("donate.fundraisingSection.statusApiNotConfigured"));
        setStatusTone("info");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setStatusMessage(null);

      try {
        const response = await axios.get(localizedUrl(donateApiUrl));
        const activeCampaigns = getCampaignsFromPayload(response.data)
          .map(normalizeCampaign)
          .filter((campaign) => campaign?.isActive && !campaign.isDeleted);

        if (!cancelled) {
          if (activeCampaigns.length) {
            setFundraisingData(activeCampaigns);
            setStatusMessage(null);
          } else {
            setFundraisingData(fallbackCampaigns);
            setStatusMessage(t("donate.fundraisingSection.statusNoActiveCampaigns"));
            setStatusTone("info");
          }
        }
      } catch {
        if (!cancelled) {
          setStatusMessage(t("donate.fundraisingSection.statusError"));
          setStatusTone("error");
          setFundraisingData(fallbackCampaigns);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchFundraisingData();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="bg-[#f7faf9] py-14 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
            {t("donate.fundraisingSection.eyebrow")}
          </p>
          <h2 className="text-3xl font-semibold leading-tight text-[#14201f] md:text-5xl">
            {t("donate.fundraisingSection.heading")}
          </h2>
          <p className="mt-5 text-base leading-8 text-[#52615f] md:text-lg">
            {t("donate.fundraisingSection.description")}
          </p>
        </div>

        {isLoading ? (
          <div className="mt-10 flex h-64 items-center justify-center">
            <Loader className="mr-3 h-7 w-7 animate-spin text-[#9d1f1c]" />
            <span className="font-medium text-[#52615f]">
              {t("donate.fundraisingSection.loading")}
            </span>
          </div>
        ) : (
          <>
            {statusMessage && (
              <div
                className={`mx-auto mt-8 max-w-3xl rounded-lg border bg-white p-4 text-center text-sm ${
                  statusTone === "error"
                    ? "border-[#f2d1d1] text-[#9d1f1c]"
                    : "border-[#dfe8e6] text-[#52615f]"
                }`}
              >
                {statusMessage}
              </div>
            )}
            {fundraisingData.length > 0 ? (
              <div className="mt-10 flex flex-wrap justify-center gap-6">
                {fundraisingData.map((card) => (
                  <div
                    key={card.id}
                    className="w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)]"
                  >
                    <FundraisingCard {...card} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mx-auto mt-10 max-w-2xl rounded-lg border border-[#dfe8e6] bg-white p-8 text-center">
                <h3 className="text-2xl font-semibold text-[#14201f]">
                  {t("donate.fundraisingSection.noCampaignsTitle")}
                </h3>
                <p className="mt-3 text-sm leading-7 text-[#52615f]">
                  {t("donate.fundraisingSection.noCampaignsText")}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default FundraisingSection;
